<template>
  <div id="app">
    <!-- 微信浏览器引导遮罩 -->
    <div v-if="showWeixinGuide" class="weixin-guide-mask" @click="hideWeixinGuide">
      <div class="guide-content" @click.stop>
        <div class="guide-arrow"></div>
        <div class="guide-text">
          <p>请点击右上角</p>
          <p>选择"在浏览器中打开"</p>
          <p class="guide-subtext">然后即可下载应用</p>
        </div>
        <div class="guide-close" @click="hideWeixinGuide">×</div>
      </div>
    </div>

    <div
      class="container"
      @wheel="handleWheel"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- 导航点 -->
      <nav class="nav-dots">
        <div
          v-for="(page, index) in pages"
          :key="index"
          class="nav-dot"
          :class="{ active: currentPage === index }"
          @click="goToPage(index)"
        ></div>
      </nav>

      <!-- 页面容器 -->
      <div class="pages-container" :style="{ transform: `translateY(-${currentPage * 100}vh)` }">

        <!-- 第0页：首页 -->
        <section class="page page-hero">
          <div class="hero-content">
            <img
              src="@/assets/logo.png"
              alt="百湖校园助手 Logo"
              class="logo"
              @error="handleImageError"
            >
            <h1 class="title">百湖校园助手</h1>
            <p class="subtitle">现代化校园生活助手</p>
            <button class="download-btn" @click="goToPage(4)">
              <span>下载体验</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </button>
          </div>
          <div class="hero-bg">
            <div class="gradient-bg"></div>
            <div class="floating-icons">
              <div class="icon icon-1">📚</div>
              <div class="icon icon-2">📱</div>
              <div class="icon icon-3">🎓</div>
              <div class="icon icon-4">📊</div>
              <div class="icon icon-5">⚡</div>
            </div>
          </div>
        </section>

        <!-- 第1页：智能课程表 -->
        <section class="page page-features">
          <div class="feature-container">
            <div class="feature-content">
              <h2 class="feature-title">智能课程表</h2>
              <div class="feature-points">
                <div class="point">
                  <div class="point-icon">🎨</div>
                  <div class="point-text">美观直观的课程表显示，可定制背景</div>
                </div>
                <div class="point">
                  <div class="point-icon">💾</div>
                  <div class="point-text">本地数据缓存，无网络也能查看课程表</div>
                </div>
                <div class="point">
                  <div class="point-icon">📅</div>
                  <div class="point-text">周视图设计，课程安排一目了然</div>
                </div>
                <div class="point">
                  <div class="point-icon">📱</div>
                  <div class="point-text">桌面小组件，一键查看今日课程</div>
                </div>
              </div>
            </div>
            <div class="feature-image">
              <div class="phone-mockup">
                <img
                  src="@/assets/course_schedule.jpg"
                  alt="课程表截图"
                  class="screenshot tilted"
                  @error="handleImageError"
                >
              </div>
              <!-- 小组件示例 -->
              <div class="widget-showcase">
                <div class="widget-demo today-widget">
                  <div class="widget-header">今日课程</div>
                  <div class="widget-courses">
                    <div class="course-item course-1">
                      <span class="course-time">1-2节</span>
                      <span class="course-name">高等数学</span>
                    </div>
                    <div class="course-item course-2">
                      <span class="course-time">3-4节</span>
                      <span class="course-name">大学英语</span>
                    </div>
                    <div class="course-item course-3">
                      <span class="course-time">5-6节</span>
                      <span class="course-name">计算机基础</span>
                    </div>
                  </div>
                </div>
                <div class="widget-demo tomorrow-widget">
                  <div class="widget-split">
                    <div class="widget-half">
                      <div class="widget-header small">今天</div>
                      <div class="course-item small course-1">
                        <span class="course-time">1-2节</span>
                        <span class="course-name">高数</span>
                      </div>
                      <div class="course-item small course-2">
                        <span class="course-time">3-4节</span>
                        <span class="course-name">英语</span>
                      </div>
                    </div>
                    <div class="widget-divider"></div>
                    <div class="widget-half">
                      <div class="widget-header small">明天</div>
                      <div class="course-item small course-4">
                        <span class="course-time">1-2节</span>
                        <span class="course-name">物理</span>
                      </div>
                      <div class="course-item small course-5">
                        <span class="course-time">3-4节</span>
                        <span class="course-name">化学</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第2页：体验优化 -->
        <section class="page page-features alt">
          <div class="feature-container reverse">
            <div class="feature-content">
              <h2 class="feature-title">体验优化</h2>
              <div class="feature-points">
                <div class="point">
                  <div class="point-icon">⚡</div>
                  <div class="point-text">优化网络请求，告别卡顿等待</div>
                </div>
                <div class="point">
                  <div class="point-icon">🔄</div>
                  <div class="point-text">数据自动更新，信息实时同步</div>
                </div>
                <div class="point">
                  <div class="point-icon">🎯</div>
                  <div class="point-text">智能缓存策略，流畅使用体验</div>
                </div>
              </div>
            </div>
            <div class="feature-image">
              <div class="phone-mockup">
                <img
                  src="@/assets/tools.jpg"
                  alt="工具页面截图"
                  class="screenshot"
                  @error="handleImageError"
                >
              </div>
            </div>
          </div>
        </section>

        <!-- 第3页：隐私保护 -->
        <section class="page page-features">
          <div class="feature-container">
            <div class="feature-content">
              <h2 class="feature-title">隐私保护</h2>
              <div class="feature-points">
                <div class="point">
                  <div class="point-icon">🔐</div>
                  <div class="point-text">本地加密存储，保护个人隐私</div>
                </div>
                <div class="point">
                  <div class="point-icon">🏫</div>
                  <div class="point-text">数据来自官方教务且本地处理，不获取任何信息</div>
                </div>
                <div class="point">
                  <div class="point-icon">🛡️</div>
                  <div class="point-text">零云端存储，数据完全掌控在你手中</div>
                </div>
              </div>
            </div>
            <div class="feature-image">
              <div class="privacy-visual">
                <div class="shield-icon">
                  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="#1976D2" stroke-width="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <div class="privacy-rings">
                  <div class="ring ring-1"></div>
                  <div class="ring ring-2"></div>
                  <div class="ring ring-3"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第4页：下载页面 -->
        <section class="page page-download">
          <!-- 版本号角标 -->
          <div class="version-badge">{{ appConfigVersion }}</div>

          <div class="download-content">
            <h2 class="download-title">立即下载</h2>
            <p class="download-subtitle">选择你的设备平台，开始更便捷的校园生活</p>

            <div class="download-buttons">
              <a
                :href="appConfig.downloadUrl"
                class="download-btn-platform android"
                @click="handleDownloadClick"
                download
              >
                <img src="@/assets/android.png" alt="Android" class="platform-icon">
                <div class="btn-text">
                  <span class="platform-name">Android</span>
                  <span class="platform-desc">v{{ androidVersion }} 立即下载</span>
                </div>
              </a>

              <a
                href="https://apps.apple.com/cn/app/%E7%99%BE%E6%B9%96%E5%8C%BB%E5%A4%A7%E5%B0%8F%E5%8A%A9%E6%89%8B/id6753145462"
                target="_blank"
                class="download-btn-platform ios"
              >
                <img src="@/assets/apple.png" alt="iOS" class="platform-icon">
                <div class="btn-text">
                  <span class="platform-name">iOS</span>
                  <span class="platform-desc">App Store v{{ iosVersion }}</span>
                </div>
              </a>

              <a
                href="https://appgallery.huawei.com/app/detail?id=cn.foxhank.hmudqpro"
                target="_blank"
                class="download-btn-platform harmonyos"
              >
                <img src="@/assets/harmonyos.png" alt="HarmonyOS" class="platform-icon">
                <div class="btn-text">
                  <span class="platform-name">HarmonyOS</span>
                  <span class="platform-desc">华为应用市场</span>
                </div>
              </a>
            </div>

  
            <div class="download-info">
              <div class="info-item">
                <span class="info-label">版本号：</span>
                <span class="info-value">{{ appConfigVersion }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">大小：</span>
                <span class="info-value">{{ appConfig.size }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">支持系统：</span>
                <span class="info-value">{{ appConfig.systemRequirement }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">开源地址：</span>
                <a :href="appConfig.sourceUrl" target="_blank" class="info-value link">Gitee 仓库</a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      currentPage: 0,
      pages: ['首页', '智能课程表', '体验优化', '隐私保护', '下载'],
      showTooltip: false,
      touchStartY: 0,
      touchStartTime: 0,
      isScrolling: false,
      showWeixinGuide: false,
      // 应用配置信息，方便统一管理
      appConfig: {
        downloadUrl: 'https://cnb.cool/foxhank/hydqpro/-/releases/download/2.1.0/HmuDqPro-2.1.0.apk',
        size: '~5MB',
        systemRequirement: 'Android 7.0+ / iOS 15.7+ / HarmonyOS 5.0.5+',
        sourceUrl: 'https://gitee.com/hank-fox/hmudqpro'
      },
      // 各平台最新版本号（默认值，接口获取失败时兜底）
      androidVersion: '2.1.0',
      iosVersion: '1.3',
      harmonyVersion: '1.0'
    }
  },
  computed: {
    // 组合展示各平台版本号
    appConfigVersion() {
      return `Android v${this.androidVersion} / iOS v${this.iosVersion} / HarmonyOS v${this.harmonyVersion}`
    }
  },
  methods: {
    goToPage(index) {
      if (index >= 0 && index < this.pages.length) {
        this.currentPage = index
      }
    },
    handleWheel(e) {
      if (this.isScrolling) return

      this.isScrolling = true

      if (e.deltaY > 0 && this.currentPage < this.pages.length - 1) {
        this.currentPage++
      } else if (e.deltaY < 0 && this.currentPage > 0) {
        this.currentPage--
      }

      setTimeout(() => {
        this.isScrolling = false
      }, 800)
    },
    handleTouchStart(e) {
      this.touchStartY = e.touches[0].clientY
      this.touchStartTime = Date.now()
    },
    handleTouchMove(e) {
      e.preventDefault()
    },
    handleTouchEnd(e) {
      if (this.isScrolling) return

      const touchEndY = e.changedTouches[0].clientY
      const touchEndTime = Date.now()
      const deltaY = this.touchStartY - touchEndY
      const deltaTime = touchEndTime - this.touchStartTime

      if (Math.abs(deltaY) > 50 && deltaTime < 500) {
        this.isScrolling = true

        if (deltaY > 0 && this.currentPage < this.pages.length - 1) {
          this.currentPage++
        } else if (deltaY < 0 && this.currentPage > 0) {
          this.currentPage--
        }

        setTimeout(() => {
          this.isScrolling = false
        }, 800)
      }
    },
    handleImageError(e) {
      console.warn('图片加载失败:', e.target.src)
    },
    // 检测是否为微信浏览器
    isWeixinBrowser() {
      const ua = navigator.userAgent.toLowerCase()
      return /micromessenger/i.test(ua)
    },
    // 处理下载点击事件
    handleDownloadClick(e) {
      if (this.isWeixinBrowser()) {
        e.preventDefault()
        this.showWeixinGuide = true
        // 添加URL参数用于后续自动下载
        const currentUrl = new URL(window.location.href)
        currentUrl.searchParams.set('download', 'true')
        window.history.replaceState({}, '', currentUrl.toString())
      }
    },
    // 隐藏微信引导遮罩
    hideWeixinGuide() {
      this.showWeixinGuide = false
    },
    // 从接口获取各平台最新版本信息
    async fetchVersionInfo() {
      // Android：从更新接口获取下载链接和版本号
      try {
        const res = await fetch('https://hydqapi.foxhank.cn/update?platform=android')
        const data = await res.json()
        if (data && data.version && data.downloadlink) {
          this.androidVersion = data.version
          this.appConfig.downloadUrl = data.downloadlink
        }
      } catch (err) {
        console.warn('获取安卓版本信息失败，使用默认值', err)
      }

      // iOS：从 App Store 查询接口获取最新版本号
      try {
        const res = await fetch('https://itunes.apple.com/lookup?id=6753145462')
        const data = await res.json()
        if (data.results && data.results.length > 0) {
          this.iosVersion = data.results[0].version
        }
      } catch (err) {
        console.warn('获取iOS版本信息失败，使用默认值', err)
      }
    },
    // 检查URL参数，如果有download=true则自动开始下载
    checkAutoDownload() {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('download') === 'true' && !this.isWeixinBrowser()) {
        // 在非微信浏览器中自动开始下载
        setTimeout(() => {
          const link = document.createElement('a')
          link.href = this.appConfig.downloadUrl
          link.download = ''
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          // 清除URL参数
          const currentUrl = new URL(window.location.href)
          currentUrl.searchParams.delete('download')
          window.history.replaceState({}, '', currentUrl.toString())
        }, 1000) // 延迟1秒开始下载，让页面加载完成
      }
    }
  },
  mounted() {
    // 禁止默认的滚动行为
    document.body.style.overflow = 'hidden'
    // 获取各平台最新版本信息
    this.fetchVersionInfo()
    // 检查是否需要自动下载
    this.checkAutoDownload()
  },
  beforeUnmount() {
    document.body.style.overflow = 'auto'
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  overflow: hidden;
  height: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  height: 100vh;
  overflow: hidden;
}

.container {
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.pages-container {
  transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  height: 100%;
}

.page {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 5%;
  overflow: hidden;
}

/* 微信引导遮罩样式 */
.weixin-guide-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 20px;
}

.guide-content {
  position: relative;
  background: transparent;
  color: white;
  text-align: center;
  max-width: 200px;
  margin-top: 60px;
}

.guide-arrow {
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-bottom: 30px solid white;
  margin: 0 auto 10px;
  position: relative;
}

.guide-arrow::after {
  content: '';
  position: absolute;
  top: 5px;
  left: -15px;
  width: 0;
  height: 0;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-bottom: 20px solid rgba(0, 0, 0, 0.8);
}

.guide-text p {
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 5px;
  font-weight: 600;
}

.guide-subtext {
  font-size: 14px !important;
  opacity: 0.8;
  margin-top: 10px !important;
}

.guide-close {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.guide-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 导航点 */
.nav-dots {
  position: fixed;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nav-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-dot.active {
  background: #1976D2;
  transform: scale(1.5);
}

/* 首页样式 */
.page-hero {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  color: white;
}

.hero-content {
  text-align: center;
  z-index: 2;
  position: relative;
}

.logo {
  width: 120px;
  height: 120px;
  margin-bottom: 24px;
  border-radius: 24px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(45deg, #fff, #e3f2fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 40px;
  font-weight: 300;
}

.download-btn {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 16px 32px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.download-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
}

.floating-icons {
  position: absolute;
  width: 100%;
  height: 100%;
}

.icon {
  position: absolute;
  font-size: 2rem;
  opacity: 0.1;
  animation: float 6s ease-in-out infinite;
}

.icon-1 { top: 20%; left: 15%; animation-delay: 0s; }
.icon-2 { top: 15%; right: 20%; animation-delay: 1s; }
.icon-3 { bottom: 25%; left: 10%; animation-delay: 2s; }
.icon-4 { bottom: 15%; right: 15%; animation-delay: 3s; }
.icon-5 { top: 60%; left: 70%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

/* 功能页面样式 */
.page-features {
  background: #fafafa;
}

.page-features.alt {
  background: #f5f5f5;
}

.feature-container {
  display: flex;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  gap: 80px;
  max-height: 90vh;
  overflow: hidden;
}

.feature-container.reverse {
  flex-direction: row-reverse;
}

.feature-content {
  flex: 1;
  padding: 0 20px;
  max-height: 100%;
  overflow-y: auto;
}

.feature-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1976D2;
  margin-bottom: 32px;
}

.feature-points {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.point {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.point:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.point-icon {
  font-size: 1.5rem;
  width: 48px;
  height: 48px;
  background: linear-gradient(45deg, #1976D2, #1565C0);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.point-text {
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
  line-height: 1.5;
}

.feature-image {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 40px;
  max-height: 100%;
  overflow: hidden;
}

.phone-mockup {
  position: relative;
  max-width: 300px;
  width: 100%;
}

.screenshot {
  width: 100%;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.screenshot.tilted {
  transform: rotate(-15deg) perspective(800px) rotateY(-15deg);
}

/* 小组件展示样式 */
.widget-showcase {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.widget-demo {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 12px 40px rgba(25, 118, 210, 0.3);
  color: white;
  transform: perspective(800px) rotateX(5deg) rotateY(-5deg);
  transition: all 0.3s ease;
}

.widget-demo:hover {
  transform: perspective(800px) rotateX(0deg) rotateY(0deg) scale(1.05);
  box-shadow: 0 16px 50px rgba(25, 118, 210, 0.4);
}

.today-widget {
  width: 160px;
  min-height: 200px;
}

.tomorrow-widget {
  width: 300px;
  min-height: 120px;
}

.widget-header {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-align: center;
  opacity: 0.9;
}

.widget-header.small {
  font-size: 12px;
  margin-bottom: 8px;
}

.widget-courses {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget-split {
  display: flex;
  align-items: stretch;
  gap: 16px;
  height: 100%;
}

.widget-half {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.widget-divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.3);
  align-self: stretch;
}

.course-item {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.course-item:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.02);
}

.course-item.small {
  padding: 6px;
  font-size: 11px;
}

.course-time {
  display: block;
  font-size: 10px;
  opacity: 0.8;
  margin-bottom: 2px;
}

.course-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
}

.course-item.small .course-time {
  font-size: 9px;
}

.course-item.small .course-name {
  font-size: 10px;
}

/* 课程颜色区分 */
.course-1 { border-left: 4px solid #F3BA75; }
.course-2 { border-left: 4px solid #72D9D3; }
.course-3 { border-left: 4px solid #74A8F7; }
.course-4 { border-left: 4px solid #F6869A; }
.course-5 { border-left: 4px solid #A2C1CA; }

/* 隐私保护页面 */
.privacy-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  height: 300px;
}

.shield-icon {
  z-index: 2;
  filter: drop-shadow(0 8px 16px rgba(25, 118, 210, 0.3));
}

.privacy-rings {
  position: absolute;
  width: 100%;
  height: 100%;
}

.ring {
  position: absolute;
  border: 2px solid rgba(25, 118, 210, 0.2);
  border-radius: 50%;
  animation: pulse 3s ease-in-out infinite;
}

.ring-1 {
  width: 150px;
  height: 150px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 0s;
}

.ring-2 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 1s;
}

.ring-3 {
  width: 250px;
  height: 250px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 2s;
}

@keyframes pulse {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
  50% { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
}

/* 下载页面样式 */
.page-download {
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
  position: relative;
}

/* 版本号角标 */
.version-badge {
  position: absolute;
  top: 30px;
  right: 30px;
  background: linear-gradient(135deg, #1976D2, #1565C0);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
  z-index: 10;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.download-content {
  text-align: center;
  max-width: 800px;
  width: 100%;
  padding: 20px;
}

.download-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1976D2;
  margin-bottom: 16px;
}

.download-subtitle {
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 48px;
  font-weight: 300;
}

.download-buttons {
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-bottom: 48px;
  flex-wrap: wrap;
}

.download-btn-platform {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  position: relative;
  min-width: 200px;
}

.download-btn-platform:hover:not(.disabled) {
  transform: translateY(-4px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.download-btn-platform.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.download-btn-platform.android:hover {
  box-shadow: 0 12px 48px rgba(76, 175, 80, 0.25);
}

.download-btn-platform.ios:hover {
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
}

.download-btn-platform.harmonyos:hover {
  box-shadow: 0 12px 48px rgba(72, 120, 238, 0.6);
}


.platform-icon {
  width: 48px;
  height: 48px;
}

.btn-text {
  text-align: left;
}

.platform-name {
  display: block;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.platform-desc {
  display: block;
  font-size: 0.9rem;
  color: #666;
}

.tooltip {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  white-space: nowrap;
  z-index: 1000;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #333;
}

.download-info {
  display: flex;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.info-label {
  color: #666;
}

.info-value {
  color: #333;
  font-weight: 600;
}

.link {
  color: #1976D2;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}


/* 移动端适配 */
@media (max-width: 768px) {
  .page {
    padding: 20px 15px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .title {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 0.9rem;
  }

  .feature-container {
    flex-direction: column;
    gap: 30px;
    text-align: center;
    max-height: calc(100vh - 40px);
    padding: 20px 0;
  }

  .feature-container.reverse {
    flex-direction: column;
  }

  .feature-content {
    padding: 0;
    max-height: auto;
    overflow-y: visible;
  }

  .feature-title {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }

  .feature-points {
    gap: 16px;
  }

  .point {
    padding: 12px;
    gap: 12px;
  }

  .point-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .point-text {
    font-size: 0.9rem;
  }

  .phone-mockup {
    max-width: 200px;
  }

  .widget-showcase {
    flex-direction: row;
    gap: 12px;
    scale: 0.8;
  }

  .today-widget {
    width: 120px;
    min-height: 150px;
  }

  .tomorrow-widget {
    width: 200px;
    min-height: 80px;
  }

  .download-buttons {
    flex-direction: column;
    align-items: center;
  }

  .download-btn-platform {
    width: 100%;
    max-width: 280px;
    padding: 16px 20px;
  }

  .download-info {
    flex-direction: column;
    gap: 12px;
  }

  .nav-dots {
    right: 10px;
    gap: 8px;
  }

  .nav-dot {
    width: 6px;
    height: 6px;
  }

  .version-badge {
    top: 10px;
    right: 10px;
    font-size: 12px;
    padding: 6px 12px;
  }

  .download-title {
    font-size: 1.8rem;
  }

  .download-subtitle {
    font-size: 1rem;
    margin-bottom: 30px;
  }

  .privacy-visual {
    width: 200px;
    height: 200px;
  }

  .shield-icon svg {
    width: 80px;
    height: 80px;
  }

  .logo {
    width: 80px;
    height: 80px;
  }

  
  /* 移动端微信引导遮罩调整 */
  .weixin-guide-mask {
    padding: 15px;
  }

  .guide-content {
    margin-top: 40px;
    max-width: 180px;
  }

  .guide-text p {
    font-size: 14px;
  }

  .guide-subtext {
    font-size: 12px !important;
  }
}

@media (max-width: 480px) {
  .title {
    font-size: 1.8rem;
  }

  .feature-title {
    font-size: 1.5rem;
  }

  .point {
    padding: 10px;
  }

  .point-text {
    font-size: 0.85rem;
  }

  .widget-showcase {
    scale: 0.7;
  }

  .download-btn-platform {
    min-width: unset;
    width: 90%;
  }

  .platform-icon {
    width: 36px;
    height: 36px;
  }

  .platform-name {
    font-size: 1rem;
  }

  .platform-desc {
    font-size: 0.8rem;
  }
}
</style>